// Usage examples:
{
  // id different than overlay-name, both need to be unique. prevents page jump on open
  // id should be used for targets
  /* <overlay-component id="myOverlay" overlay-name="overlay-left" buttons-outside-context="true">
  <div class="overlay-container" data-overlay-container>
    <div class="overlay-panel" data-animation-direction="left">
      {% render 'overlay-header' %}
      <h2 class="overlay-title">Left Slide-In</h2>
      <div class="overlay-body" data-overlay-content>
        <p>This overlay slides in from the left side of the screen.</p>
      </div>

      </div>
    </div>
      
    <button class="overlay-trigger" data-target="myOverlay">Open Left Overlay</button>
</overlay-component>

<overlay-component id="myOverlay2" overlay-name="overlay-right" buttonsOutsideContext="false">
  <div class="overlay-container" data-overlay-container="myOverlay2">
    <div class="overlay-panel" data-animation-direction="right">
      {% render 'overlay-header' %}
      <h2 class="overlay-title">Right Slide-In</h2>
      <div class="overlay-body" data-overlay-content>
        <p>This overlay slides in from the right side of the screen.</p>
      </div>

      </div>
    </div>
    <button class="overlay-trigger" data-target="myOverlay2">Open Right Overlay</button>
</overlay-component>

<overlay-component id="myOverlay3" overlay-name="overlay-center" buttonsOutsideContext="true">
  <div class="overlay-container" data-overlay-container="myOverlay3">
    <div class="overlay-panel" data-animation-direction="center">
      {% render 'overlay-header' %}
      <h2 class="overlay-title">Center Fade-In</h2>
      <div class="overlay-body" data-overlay-content>
        <p>This overlay fades in from the center of the screen.</p>
      </div>
      </div>
    </div>

    <button class="overlay-trigger" data-target="myOverlay3">Open Center Overlay</button>
</overlay-component> */
}

class OverlayComponent extends HTMLElement {
  constructor() {
    super();
    this.overlayHasInitialized = false;

    this.overlay =
      document.querySelector(`[data-overlay-container="${this.id}"]`) || this.querySelector('.overlay-container');
    this.innerOverlay =
      this.overlay?.querySelector(`[data-overlay-container]`) || this.overlay?.querySelector('.overlay-container');
    this.closeButtons = this.querySelectorAll('.overlay-close');
    this.triggerButtons = this.querySelectorAll(`.overlay-trigger[data-target="${this.id}"]`);
    this.overlayName = this.getAttribute('overlay-name') || 'overlay';
    this.buttonsOutsideContext = this.getAttribute('buttons-outside-context') || 'false';
    if (this.buttonsOutsideContext == 'true') {
      this.triggerButtons = document.querySelectorAll(`.overlay-trigger[data-target="${this.id}"]`);
    } else {
      this.triggerButtons = this.querySelectorAll(`.overlay-trigger[data-target="${this.id}"]`);
    }

    if (this.overlay.dataset.overlayContainer === 'header-search-overlay') {
      this.isSearchOverlay = true;
    }

    if (!this.overlay) return;

    this.moveOverlayToBody();
    this.setupEventListeners();
    this.checkUrlHash();
  }

  getEasingFromCSSVariable() {
    const rootStyle = getComputedStyle(document.documentElement);
    return rootStyle.getPropertyValue('--ease-smooth').trim() || 'ease';
  }

  setupEventListeners() {
    this.overlay.addEventListener('click', this.onOverlayClick.bind(this));
    if (this.closeButtons?.length) {
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', this.closeOverlay.bind(this));
      });
    }
    this.triggerButtons.forEach((button) => {
      button.addEventListener('click', this.openOverlay.bind(this));
    });
    window.addEventListener('keyup', this.onKeyUp.bind(this));
    window.addEventListener('hashchange', this.checkUrlHash.bind(this));
  }

  checkUrlHash() {
    const hash = window.location.hash.substring(1);
    if (hash === this.overlayName) {
      this.openOverlay(false);
    }
  }

  onOverlayClick(event) {
    if (event.target === this.overlay) {
      this.closeOverlay();
    }
  }

  onKeyUp(event) {
    if (event.key === 'Escape') {
      this.closeOverlay();
    }
  }

  openOverlay(updateHash = true) {
    if (this.overlay.classList.contains('is-open')) return;

    this.overlay.classList.add('is-open');
    if (updateHash) {
      location.replace(`#${this.overlayName}`);
    }
    document.body.style.overflow = 'hidden';
    this.animateSlideIn();

    if (this.isSearchOverlay) {
      const searchInput = document.querySelector('input[data-type="search"]');
      searchInput.focus();
    }

    this.dispatchEvent(new CustomEvent('overlayOpened'));
  }

  moveOverlayToBody() {
    if (this.overlayHasInitialized) return;

    this.allOverlaysWithTheSameId = document.querySelectorAll(`[data-overlay-container="${this.id}"]`);
    const hasMoreThanOneOverlay = this.allOverlaysWithTheSameId.length > 1;

    if (hasMoreThanOneOverlay) {
      this.allOverlaysWithTheSameId.forEach((overlay, index) => {
        if (index !== 0 && overlay.parentNode.contains(overlay)) {
          overlay.parentNode.removeChild(overlay);
        }
      });
    }

    if (this.contains(this.overlay)) {
      this.removeChild(this.overlay);
    }

    document.body.appendChild(this.overlay);

    this.overlayHasInitialized = true;
  }

  closeOverlay() {
    this.animateSlideOut().then(() => {
      this.overlay.classList.remove('is-open');

      // Close inner overlay if present
      this.innerOverlay?.classList?.remove('is-open');

      if (window.location.hash === `#${this.overlayName}` && history.replaceState) {
        history.replaceState(null, null, window.location.pathname + window.location.search);
      }
      document.body.style.overflow = 'auto';
      this.dispatchEvent(new CustomEvent('overlayClosed'));
    });
  }

  animateSlideIn() {
    const panel = this.overlay.querySelector('.overlay-panel');
    const direction = panel.dataset.animationDirection;
    const easing = this.getEasingFromCSSVariable();

    if (direction === 'left' || direction === 'right') {
      const offset = direction === 'left' ? -100 : 100;
      panel.animate([{ transform: `translateX(${offset}%)` }, { transform: 'translateX(0)' }], {
        duration: 600,
        easing: easing,
        fill: 'forwards',
      });
    } else if (direction === 'center') {
      panel.style.opacity = 0;
      panel.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 700,
        easing: easing,
        fill: 'forwards',
      });
    }
  }

  animateSlideOut() {
    return new Promise((resolve) => {
      const panel = this.overlay.querySelector('.overlay-panel');
      const direction = panel.dataset.animationDirection;
      const easing = this.getEasingFromCSSVariable(); // Retrieve easing from CSS variable

      if (direction === 'left' || direction === 'right') {
        const offset = direction === 'left' ? -100 : 100;
        const animation = panel.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${offset}%)` }], {
          duration: 300,
          easing: easing,
          fill: 'forwards',
        });
        animation.onfinish = resolve;
      } else if (direction === 'center') {
        const animation = panel.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: 300,
          easing: easing,
          fill: 'forwards',
        });

        animation.onfinish = resolve;
      }
    });
  }
}

customElements.define('overlay-component', OverlayComponent);
